import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pass.length < 6) { setError("Password must be at least 6 characters long."); return; }
    if (pass !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pass });
      setDone(true);
      toast.success("Password reset — you can now sign in");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="bg-slate-900 px-6 py-5 lb-grain flex items-center gap-2 text-white">
          <ShieldCheck size={22} className="text-emerald-400" />
          <span className="font-display text-2xl" style={{ fontWeight: 800 }}>Reset password</span>
        </div>
        <div className="p-6">
          {!token ? (
            <p className="text-sm text-rose-600" data-testid="reset-no-token">This reset link is missing its token. Please use the link from your email.</p>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="text-emerald-600" />
              </div>
              <p className="font-semibold text-slate-800">Password updated</p>
              <p className="text-sm text-slate-500 mt-1 mb-4">Your password has been reset successfully.</p>
              <Button data-testid="reset-go-home" onClick={() => navigate("/")} className="bg-slate-900 hover:bg-emerald-600 rounded-full">
                Back to home & sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div data-testid="reset-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}
              <div>
                <Label>New password</Label>
                <Input data-testid="reset-password-input" type="password" required value={pass} onChange={(e) => setPass(e.target.value)} placeholder="At least 6 characters" />
                <PasswordStrengthMeter password={pass} testId="reset-password-strength" />
              </div>
              <div>
                <Label>Confirm new password</Label>
                <Input data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
              </div>
              <Button data-testid="reset-submit-btn" type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-emerald-600 rounded-full">
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}
          <p className="text-center text-sm text-slate-400 mt-5">
            <Link to="/" className="hover:text-emerald-700">Return to my360boss</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
